import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerPortalLayout from '../../components/customer-portal/CustomerPortalLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  ClipboardList, PlusCircle, ArrowRight, Clock, 
  CheckCircle2, XCircle, AlertCircle, Loader2, RotateCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function CustomerBookingRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('customer_token');
      if (!token) {
        navigate('/customer-login');
        return;
      }
      const res = await fetch(`${API}/api/client-portal/booking-requests`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) {
        throw new Error('Failed to fetch booking requests');
      }
      const data = await res.json();
      setRequests(data.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (st) => {
    const s = (st || 'PENDING').toUpperCase();
    if (s === 'CONFIRMED') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200">
          <CheckCircle2 className="w-3 h-3" />
          Confirmed
        </span>
      );
    }
    if (s === 'REJECTED' || s === 'CANCELLED') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-800 border border-red-200">
          <XCircle className="w-3 h-3" />
          {s}
        </span>
      );
    }
    if (s === 'ESTIMATE_SENT') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-800 border border-blue-200">
          Estimate Shared
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200">
        <Clock className="w-3 h-3" />
        Under Review
      </span>
    );
  };

  return (
    <CustomerPortalLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-manrope text-slate-900">Booking Inquiries &amp; Requests</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Track submitted requirements, quotation readiness, and operations review status
            </p>
          </div>
          <Button
            onClick={() => navigate('/customer-portal/request-booking')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs h-10 px-4 shadow-sm flex items-center gap-2"
          >
            <PlusCircle className="w-4 h-4" />
            <span>New Booking Request</span>
          </Button>
        </div>

        {/* Requests Table */}
        <Card className="bg-white rounded-xl border border-slate-200/80 overflow-hidden shadow-xs">
          {loading ? (
            <div className="py-16 flex flex-col items-center justify-center text-slate-400 gap-2">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="text-xs">Loading booking requests...</span>
            </div>
          ) : requests.length === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center text-center p-6 text-slate-400">
              <ClipboardList className="w-12 h-12 text-slate-300 mb-3 stroke-1" />
              <p className="text-base font-bold text-slate-800 font-manrope">No Booking Requests Submitted</p>
              <p className="text-xs text-slate-400 mt-1 max-w-sm">
                You haven't requested any bookings yet. Submit a requirement to receive competitive freight quotes.
              </p>
              <Button
                onClick={() => navigate('/customer-portal/request-booking')}
                className="mt-4 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs h-9 px-4 rounded-xl"
              >
                <PlusCircle className="w-3.5 h-3.5 mr-1.5" />
                Create Booking Request
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-semibold text-[11px]">
                  <tr>
                    <th className="px-5 py-3.5">Request No</th>
                    <th className="px-5 py-3.5">Submitted On</th>
                    <th className="px-5 py-3.5">Category &amp; Mode</th>
                    <th className="px-5 py-3.5">Route</th>
                    <th className="px-5 py-3.5">Packages &amp; Weight</th>
                    <th className="px-5 py-3.5">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {requests.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-5 py-3.5 font-bold font-mono text-slate-900">
                        {r.request_number}
                        {r.ready_date && (
                          <span className="block text-[10px] text-slate-400 font-normal font-ibm mt-0.5">
                            Ready: {r.ready_date}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-slate-600 whitespace-nowrap">
                        {r.created_at || '-'}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="font-bold text-slate-800 block">{r.category}</span>
                        <span className="text-[10px] text-slate-400 block">{r.service_type?.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1.5 text-slate-800 font-medium">
                          <span className="truncate max-w-[120px]">{r.from_location}</span>
                          <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
                          <span className="truncate max-w-[120px]">{r.to_location}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-slate-600">
                        {r.packages ? `${r.packages} Pkgs` : '-'} {r.weight ? `• ${r.weight} Kg` : ''}
                      </td>
                      <td className="px-5 py-3.5">
                        {getStatusBadge(r.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </CustomerPortalLayout>
  );
}
